<template>
    <div>
        <div v-if="isFetching && !hasError"
             class="d-flex justify-content-center align-items-center h-100 positioned-overlay"
             style="background-color: #FFFFFF; z-index: 1000;">
            <loader-wrapper/>
        </div>

        <div v-if="hasError" class="d-flex justify-content-center align-items-center h-100 positioned-overlay"
             style="background-color: #FFFFFF; color: red; font-weight: bold; font-size: 1.2rem; z-index: 1000;">
            {{ errorMessage }}
        </div>

        <video v-if="!hasError" crossOrigin="anonymous"
               v-bind:class="'video-js w-100 positioned-overlay ' + video_layout_class"
               preload playsinline="" webkit-playsinline="" :id="videoTag">
            <source src="" type="video/mp4">
        </video>

       <div v-if="!hasError" class="custom-controls-video-miniature d-flex align-items-center px-4">
            <div class="progress-bar-video-miniature ">
                <div class="progress-bar-fill" :style="{ width: progress + '%' }"></div>
            </div>
            <div ref="timeDisplay" class="time-display-video-miniature">00:00:00</div>
        </div>
        <div v-if="!hasError" class="d-flex justify-content-center align-items-center h-100 positioned-overlay"></div>
    </div>
</template>

<script setup>
import { ref, onBeforeUnmount, watch } from 'vue';
import { fetchSettings } from "@/playerVue3/services/video-service";
import LoaderWrapper from "@/components/loading/loader-wrapper.vue";

// INITIALIZE STORE & COMPOSABLES

// PROPS & EMITS
const props = defineProps({
    videoFilterOptions: {
        type: Object,
        required: true,
        default() {
            return {};
        }
    },
    video_layout_class: {
        type: String,
        required: true,
    },
});

// REACTIVE VARIABLES
const isFetching = ref(true);
const errorMessage = ref('');
const hasError = ref(false);
const player = ref(null);
const videoTag = ref('videoTag');
const progress = ref(0);
const timeDisplay = ref(null);
const videoOptions = ref({
    src: '',
    title: '',
    description: '',
    orientationClass: '',
    fps: 0,
});
const refreshIntervalId = ref(null);

// COMPUTED PROPERTIES

// METHODS
const searchVideoSettings = async () => {
    let response = {};
    videoTag.value = videoTag.value + props.videoFilterOptions.vid;
    // el parametro email se deja vacio por el momento
    response = await fetchSettings(props.videoFilterOptions.vid, props.videoFilterOptions.wid, props.videoFilterOptions.email);
    if (response) {
        const videoData = response.data.videoData;
        if (videoData.videoType === 'Vimeo') {
            videoOptions.value.src = videoData.videoVimeo;
        } else {
            // La funcion this.imagePath() se debe validar
            videoOptions.value.src = response.data.routeURL + "videos/files_hls/" + videoData.videoSrcHls;
        }
        videoOptions.value.title = videoData.title;
        videoOptions.value.description = videoData.description;
        videoOptions.value.orientationClass = props.video_layout_class;
        videoOptions.value.fps = Number(videoData.fps);

        return true;
    }
    return false;
};

const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);

    const hh = h.toString().padStart(2, '0');
    const mm = m.toString().padStart(2, '0');
    const ss = s.toString().padStart(2, '0');

    return `${hh}:${mm}:${ss}`;
};

const getRemainingTime = (duration, position) => {
    let remaining = duration - position;
    if (remaining < 0) remaining = 0;
    return formatTime(remaining);
};

const disablePictureInPicture = () => {
    setTimeout(function () {
        const jwVideos = document.querySelectorAll('.jw-video.jw-reset');

        jwVideos.forEach(function (element) {
            element.setAttribute('disablePictureInPicture', '');
        });
    }, 500);
};

async function initPlayer() {
    let aspectRatio = '16:9';
    let arrSubtitles = [];

    let response = false;
    response = await searchVideoSettings();
    if (response) {
        player.value = jwplayer(videoTag.value).setup({
            'autostart': true,
            'mute': true,
            "playlist": [{
                'aspectratio': aspectRatio,
                'dock': 'false',
                'file': videoOptions.value.src,
                'height': '100%',
                "mediaid": videoOptions.value.vid,
                "renderCaptionsNatively": "true",
                'stretching': 'uniform',
                "title": videoOptions.value.title,
                "tracks": arrSubtitles,
                'width': '100%',
            }],
        });
        player.value.setCurrentCaptions(0);
        player.value.setControls(false);

        player.value.on('ready', function () {
            const _this = this;
            clearProgressInterval(); // asegura limpiar cualquier anterior

            refreshIntervalId.value = setInterval(() => {
                const duration = player.value.getDuration();
                const position = _this.getPosition();

                if (duration > 0) {
                    progress.value = (position / duration) * 100;
                } else {
                    progress.value = 0;
                }

                if (timeDisplay.value != null) {
                    timeDisplay.value.textContent = getRemainingTime(duration, position);
                }
            }, 500);
        });

        player.value.on('error', (event) => {
            isFetching.value = false;
            hasError.value = true;
            errorMessage.value = event.message || 'Error loading video.';
        });

        disablePictureInPicture();
        isFetching.value = false;
    }

    player.value.on('complete', clearProgressInterval);
}

function clearProgressInterval() {
    if (refreshIntervalId.value) {
        clearInterval(refreshIntervalId.value);
        refreshIntervalId.value = null;
    }
}

// LYFECYCLE HOOKS
watch(() => props.videoFilterOptions.vid, () => {
    // Remove previous JWPlayer instance
    if (player.value) {
        player.value.remove();
        player.value = null;
    }
    // Clean up global jwplayer variable for the videoTag
    if (window.jwplayer && typeof window.jwplayer === 'function') {
        try {
            window.jwplayer(videoTag.value).remove();
            clearInterval(refreshIntervalId.value);
        } catch (e) {
            // Ignore if not initialized
        }
    }
    isFetching.value = true;
    // Now initialize the new player
    initPlayer();
}, { immediate: true });

onBeforeUnmount(() => {
    if (player.value) {
        player.value.remove();
        clearProgressInterval();
    }
});
</script>
